import React, { useState, useCallback } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Box, alpha, Collapse, IconButton } from "@material-ui/core";
import { Clock, Coffee, UtensilsCrossed, Timer, PauseCircle, ChevronUp, ChevronDown } from "lucide-react";
import StandardModal from "../ui/StandardModal";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: "0.85rem",
    marginBottom: theme.spacing(2.5),
    textAlign: "center",
  },
  optionsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: theme.spacing(1.5),
  },
  optionCard: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(2.5, 2),
    borderRadius: 12,
    cursor: "pointer",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    border: `1.5px solid ${
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.08)"
        : theme.palette.divider
    }`,
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.background.contrast
        : theme.palette.background.default,
    "&:hover": {
      borderColor: alpha(theme.palette.primary.main, 0.4),
      backgroundColor:
        theme.palette.type === "dark"
          ? "rgba(255, 255, 255, 0.06)"
          : alpha(theme.palette.primary.main, 0.04),
      transform: "translateY(-1px)",
    },
    "&:active": {
      transform: "translateY(0)",
    },
  },
  optionCardSelected: {
    borderColor: theme.palette.primary.main,
    borderWidth: 2,
    backgroundColor:
      theme.palette.type === "dark"
        ? alpha(theme.palette.primary.main, 0.12)
        : alpha(theme.palette.primary.main, 0.06),
    boxShadow:
      theme.palette.type === "dark"
        ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.3)}, 0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
        : `0 0 0 1px ${alpha(theme.palette.primary.main, 0.2)}, 0 4px 12px ${alpha(theme.palette.primary.main, 0.1)}`,
    "&:hover": {
      borderColor: theme.palette.primary.main,
      backgroundColor:
        theme.palette.type === "dark"
          ? alpha(theme.palette.primary.main, 0.15)
          : alpha(theme.palette.primary.main, 0.08),
    },
  },
  iconWrapper: {
    width: 40,
    height: 40,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: theme.spacing(1.2),
    backgroundColor:
      theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.06)"
        : alpha(theme.palette.primary.main, 0.08),
    transition: "all 0.2s ease",
  },
  iconWrapperSelected: {
    backgroundColor: alpha(theme.palette.primary.main, 0.15),
  },
  optionIcon: {
    width: 20,
    height: 20,
    color: theme.palette.text.secondary,
    transition: "color 0.2s ease",
  },
  optionIconSelected: {
    color: theme.palette.primary.main,
  },
  optionLabel: {
    fontWeight: 600,
    fontSize: "0.95rem",
    color: theme.palette.text.primary,
    lineHeight: 1.2,
    marginBottom: theme.spacing(0.3),
  },
  optionDescription: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    lineHeight: 1.3,
  },
  // Seletor de tempo personalizado
  customTimeContainer: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(2),
    borderRadius: 12,
    backgroundColor: theme.palette.type === "dark"
      ? theme.palette.background.contrast
      : alpha(theme.palette.primary.main, 0.02),
    border: `1px solid ${theme.palette.type === "dark"
      ? theme.palette.divider
      : alpha(theme.palette.primary.main, 0.1)}`,
  },
  customTimeLabel: {
    fontSize: "0.8rem",
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textAlign: "center",
    marginBottom: theme.spacing(1.5),
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  timePickerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(1),
  },
  timeUnit: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  spinnerButton: {
    width: 32,
    height: 32,
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 0,
    color: theme.palette.primary.main,
    backgroundColor: theme.palette.type === "dark"
      ? "rgba(255, 255, 255, 0.06)"
      : alpha(theme.palette.primary.main, 0.08),
    border: `1px solid ${theme.palette.type === "dark"
      ? "rgba(255, 255, 255, 0.1)"
      : alpha(theme.palette.primary.main, 0.15)}`,
    transition: "all 0.15s ease",
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.15),
      borderColor: alpha(theme.palette.primary.main, 0.3),
    },
    "&:disabled": {
      opacity: 0.3,
      cursor: "default",
    },
  },
  timeDisplay: {
    width: 56,
    height: 52,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 10,
    backgroundColor: theme.palette.type === "dark"
      ? "rgba(255, 255, 255, 0.06)"
      : theme.palette.background.paper,
    border: `1.5px solid ${theme.palette.type === "dark"
      ? "rgba(255, 255, 255, 0.12)"
      : alpha(theme.palette.primary.main, 0.15)}`,
    fontWeight: 700,
    fontSize: "1.4rem",
    color: theme.palette.primary.main,
    fontVariantNumeric: "tabular-nums",
    userSelect: "none",
  },
  timeSeparator: {
    fontSize: "1.5rem",
    fontWeight: 700,
    color: theme.palette.text.secondary,
    lineHeight: 1,
    marginTop: theme.spacing(0.5),
    userSelect: "none",
  },
  timeUnitLabel: {
    fontSize: "0.65rem",
    fontWeight: 600,
    color: theme.palette.text.disabled,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  totalDuration: {
    textAlign: "center",
    marginTop: theme.spacing(1.5),
    fontSize: "0.8rem",
    fontWeight: 500,
    color: theme.palette.primary.main,
    opacity: 0.8,
  },
}));

const PRESET_OPTIONS = [
  {
    value: 15,
    labelKey: "pauseModal.options.quick.label",
    descriptionKey: "pauseModal.options.quick.description",
    icon: Clock,
  },
  {
    value: 30,
    labelKey: "pauseModal.options.coffee.label",
    descriptionKey: "pauseModal.options.coffee.description",
    icon: Coffee,
  },
  {
    value: 90,
    labelKey: "pauseModal.options.lunch.label",
    descriptionKey: "pauseModal.options.lunch.description",
    icon: UtensilsCrossed,
  },
];

const MANUAL_OPTION = {
  value: "manual",
  labelKey: "pauseModal.options.manual.label",
  descriptionKey: "pauseModal.options.manual.description",
  icon: Timer,
};

const PauseModal = ({ open, onClose, onConfirm }) => {
  const classes = useStyles();
  const [selectedOption, setSelectedOption] = useState(null);
  const [customHours, setCustomHours] = useState(0);
  const [customMinutes, setCustomMinutes] = useState(30);

  const isManualSelected = selectedOption === "manual";

  // Calcular duração final em minutos
  const getFinalDuration = useCallback(() => {
    if (selectedOption === "manual") {
      return customHours * 60 + customMinutes;
    }
    return selectedOption;
  }, [selectedOption, customHours, customMinutes]);

  const canConfirm = selectedOption !== null && (
    selectedOption !== "manual" || (customHours * 60 + customMinutes) > 0
  );

  const handleClose = useCallback(() => {
    setSelectedOption(null);
    setCustomHours(0);
    setCustomMinutes(30);
    onClose();
  }, [onClose]);

  const handleConfirm = useCallback(() => {
    if (canConfirm) {
      onConfirm(getFinalDuration());
      setSelectedOption(null);
      setCustomHours(0);
      setCustomMinutes(30);
    }
  }, [canConfirm, getFinalDuration, onConfirm]);

  const handleSelectOption = useCallback((value) => {
    setSelectedOption(value);
  }, []);

  const incrementHours = useCallback(() => {
    setCustomHours((prev) => {
      const next = Math.min(prev + 1, 8);
      // Se atingiu o máximo de horas, zerar minutos para não exceder 480min
      if (next === 8) {
        setCustomMinutes(0);
      }
      return next;
    });
  }, []);

  const decrementHours = useCallback(() => {
    setCustomHours((prev) => Math.max(prev - 1, 0));
  }, []);

  const incrementMinutes = useCallback(() => {
    // Não permitir incrementar minutos quando já está no máximo (8h)
    if (customHours >= 8) return;

    setCustomMinutes((prev) => {
      const next = prev + 15;
      if (next >= 60) {
        // Overflow para a próxima hora (se não atingir o máximo)
        if (customHours < 7) {
          // Seguro: próxima hora será no máximo 7, longe do limite
          setCustomHours((h) => h + 1);
          return 0;
        }
        if (customHours === 7) {
          // Próxima hora será 8 (limite): forçar minutos para 0
          setCustomHours(8);
          return 0;
        }
        return prev; // Não pode mais incrementar
      }
      return next;
    });
  }, [customHours]);

  const decrementMinutes = useCallback(() => {
    setCustomMinutes((prev) => {
      const next = prev - 15;
      if (next < 0) {
        // Underflow para a hora anterior
        if (customHours > 0) {
          setCustomHours((h) => h - 1);
          return 45;
        }
        return prev; // Não pode mais decrementar
      }
      return next;
    });
  }, [customHours]);

  // Formatar duração para exibição
  const formatCustomDuration = () => {
    const total = customHours * 60 + customMinutes;
    if (total === 0) return i18n.t("pauseModal.customTime.selectDuration") || "Selecione a duração";
    const parts = [];
    if (customHours > 0) parts.push(`${customHours}h`);
    if (customMinutes > 0) parts.push(`${customMinutes}min`);
    return `${i18n.t("pauseModal.customTime.totalLabel") || "Total"}: ${parts.join(" ")}`;
  };

  return (
    <StandardModal
      open={open}
      onClose={handleClose}
      title={i18n.t("pauseModal.title")}
      titleIcon={<PauseCircle />}
      maxWidth="xs"
      primaryButton={{
        text: i18n.t("pauseModal.confirm"),
        color: "primary",
        onClick: handleConfirm,
        visible: true,
        disabled: !canConfirm,
      }}
      secondaryButton={{
        text: i18n.t("pauseModal.cancel"),
        visible: true,
      }}
      closeOnConfirm={false}
    >
      <Typography className={classes.subtitle}>
        {i18n.t("pauseModal.subtitle")}
      </Typography>

      <Box className={classes.optionsGrid}>
        {/* Opções preset (15, 30, 90 min) */}
        {PRESET_OPTIONS.map((option) => {
          const IconComponent = option.icon;
          const isSelected = selectedOption === option.value;

          return (
            <Box
              key={option.value}
              className={`${classes.optionCard} ${
                isSelected ? classes.optionCardSelected : ""
              }`}
              onClick={() => handleSelectOption(option.value)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleSelectOption(option.value);
                }
              }}
              aria-pressed={isSelected}
              aria-label={i18n.t(option.labelKey)}
            >
              <Box
                className={`${classes.iconWrapper} ${
                  isSelected ? classes.iconWrapperSelected : ""
                }`}
              >
                <IconComponent
                  className={`${classes.optionIcon} ${
                    isSelected ? classes.optionIconSelected : ""
                  }`}
                  strokeWidth={isSelected ? 2.2 : 1.8}
                />
              </Box>
              <Typography className={classes.optionLabel}>
                {i18n.t(option.labelKey)}
              </Typography>
              <Typography className={classes.optionDescription}>
                {i18n.t(option.descriptionKey)}
              </Typography>
            </Box>
          );
        })}

        {/* Opção Manual */}
        {(() => {
          const IconComponent = MANUAL_OPTION.icon;
          return (
            <Box
              className={`${classes.optionCard} ${
                isManualSelected ? classes.optionCardSelected : ""
              }`}
              onClick={() => handleSelectOption("manual")}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleSelectOption("manual");
                }
              }}
              aria-pressed={isManualSelected}
              aria-label={i18n.t(MANUAL_OPTION.labelKey)}
            >
              <Box
                className={`${classes.iconWrapper} ${
                  isManualSelected ? classes.iconWrapperSelected : ""
                }`}
              >
                <IconComponent
                  className={`${classes.optionIcon} ${
                    isManualSelected ? classes.optionIconSelected : ""
                  }`}
                  strokeWidth={isManualSelected ? 2.2 : 1.8}
                />
              </Box>
              <Typography className={classes.optionLabel}>
                {i18n.t(MANUAL_OPTION.labelKey)}
              </Typography>
              <Typography className={classes.optionDescription}>
                {i18n.t(MANUAL_OPTION.descriptionKey)}
              </Typography>
            </Box>
          );
        })()}
      </Box>

      {/* Seletor de tempo personalizado */}
      <Collapse in={isManualSelected} timeout={300}>
        <Box className={classes.customTimeContainer}>
          <Typography className={classes.customTimeLabel}>
            {i18n.t("pauseModal.customTime.title") || "Defina a duração"}
          </Typography>

          <Box className={classes.timePickerRow}>
            {/* Horas */}
            <Box className={classes.timeUnit}>
              <IconButton
                className={classes.spinnerButton}
                onClick={incrementHours}
                disabled={customHours >= 8}
                size="small"
                aria-label="Aumentar horas"
              >
                <ChevronUp size={16} />
              </IconButton>
              <Box className={classes.timeDisplay}>
                {String(customHours).padStart(2, "0")}
              </Box>
              <IconButton
                className={classes.spinnerButton}
                onClick={decrementHours}
                disabled={customHours <= 0}
                size="small"
                aria-label="Diminuir horas"
              >
                <ChevronDown size={16} />
              </IconButton>
              <Typography className={classes.timeUnitLabel}>
                {i18n.t("pauseModal.customTime.hours") || "horas"}
              </Typography>
            </Box>

            {/* Separador */}
            <Typography className={classes.timeSeparator}>:</Typography>

            {/* Minutos */}
            <Box className={classes.timeUnit}>
              <IconButton
                className={classes.spinnerButton}
                onClick={incrementMinutes}
                disabled={customHours >= 8}
                size="small"
                aria-label="Aumentar minutos"
              >
                <ChevronUp size={16} />
              </IconButton>
              <Box className={classes.timeDisplay}>
                {String(customMinutes).padStart(2, "0")}
              </Box>
              <IconButton
                className={classes.spinnerButton}
                onClick={decrementMinutes}
                disabled={customHours <= 0 && customMinutes <= 0}
                size="small"
                aria-label="Diminuir minutos"
              >
                <ChevronDown size={16} />
              </IconButton>
              <Typography className={classes.timeUnitLabel}>
                {i18n.t("pauseModal.customTime.minutes") || "minutos"}
              </Typography>
            </Box>
          </Box>

          {/* Total calculado */}
          <Typography className={classes.totalDuration}>
            {formatCustomDuration()}
          </Typography>
        </Box>
      </Collapse>
    </StandardModal>
  );
};

export default PauseModal;
